//
// Shared masthead auth-state affordance, imported by every page (one
// `<script type="module" src="/app/assets/masthead.js">` tag each) so the
// nav item is populated identically everywhere instead of being
// reimplemented per page.
//
// The `haiku_sid` session cookie is HttpOnly (server-set) -- JS cannot read
// it directly, so the ONLY way to know the signed-in state is a `GET /me`
// fetch (which sends the cookie automatically); this module never inspects
// `document.cookie` for that purpose.
//
// Signed in  -> "@handle" linking to /app/account.html.
// Signed out (or the /me fetch itself fails) -> "Sign in" linking to
// /auth/login. Existing nav items (`data-testid="nav-compose"` etc.) are
// left untouched; this only appends one more `<a data-testid="nav-account">`.
//
import { getJSON } from "./api.js";
import { initThemePicker } from "./themes/picker.js";
import type { Principal } from "./types.js";

function appendAccountLink(nav: Element): HTMLAnchorElement {
  const a = document.createElement("a");
  a.dataset.testid = "nav-account";
  nav.append(a);
  return a;
}

function renderSignedOut(a: HTMLAnchorElement): void {
  a.textContent = "Sign in";
  a.href = "/auth/login";
}

function renderSignedIn(a: HTMLAnchorElement, me: Principal): void {
  // `handle` can be unset right after a first sign-in (it's assigned via
  // Task 6's settings page) -- still signed in, so still link to the
  // account page, just without a handle to display yet.
  a.textContent = me.handle ? "@" + me.handle : "account";
  a.href = "/app/account.html";
}

export async function initMasthead(): Promise<void> {
  const nav = document.querySelector('header[role="banner"] nav[aria-label="primary"]');
  if (!nav) return;
  const a = appendAccountLink(nav);
  try {
    const me = await getJSON<Principal>("/me");
    if (me && me.id) {
      renderSignedIn(a, me);
    } else {
      renderSignedOut(a);
    }
  } catch {
    renderSignedOut(a);
  }
}

void initMasthead();
initThemePicker();
